// Admin › Platform: nventr.io feature health. One synthetic journey per
// feature (backend/shared/platformHealth/journeys.js) runs every 15 min as a
// probe member; this board reads platformHealth/* through GET /platform/health.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { apiGet, apiPost } from '../utils/backendApi';

const MODULE_LABELS = {
  site: 'Public site', home: 'Console home', solutions: 'Solutions', builder: 'App Builder', knowledge: 'Knowledge', data: 'Data',
  workflows: 'Workflows', integrations: 'Integrations', 'human-review': 'Human Review', qa: 'QA & Testing', deployments: 'Deployments',
  nova: 'NoVA', analytics: 'Analytics', administration: 'Administration',
};
const MODULE_ORDER = Object.keys(MODULE_LABELS);
const KIND_HELP = { http: 'HTTP', browser: 'browser', rules: 'rules', api: 'in-process', model: 'model' };

const fmtAgo = (ts) => {
  if (!ts) return 'never';
  const s = Math.round((Date.now() - ts) / 1000);
  if (s < 90) return `${s}s ago`;
  if (s < 5400) return `${Math.round(s / 60)}m ago`;
  if (s < 172800) return `${Math.round(s / 3600)}h ago`;
  return `${Math.round(s / 86400)}d ago`;
};
const fmtMs = (ms) => (ms >= 1000 ? `${(ms / 1000).toFixed(ms >= 10000 ? 0 : 1)}s` : `${ms || 0}ms`);

export default function AdminPlatformHealth({ notify }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [open, setOpen] = useState(null);     // module id
  const [run, setRun] = useState(null);       // expanded historical run
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => apiGet('/platform/health').then((d) => { setData(d); setErr(null); }).catch((e) => setErr(e.message)), []);
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);

  const onRun = async () => {
    setBusy(true);
    try { const r = await apiPost('/platform/health/run', {}); notify?.(`Run ${r.jobId} queued — results land in ~5 min.`); setTimeout(load, 3000); }
    catch (e) { notify?.(e.message); }
    finally { setBusy(false); }
  };

  const modules = useMemo(() => {
    const byMod = {};
    for (const j of data?.registry || []) (byMod[j.module] = byMod[j.module] || []).push(j);
    return MODULE_ORDER.filter((m) => byMod[m]).map((m) => {
      const js = byMod[m];
      const failing = js.filter((j) => j.ok === false);
      const never = js.filter((j) => j.ok === undefined);
      return { id: m, label: MODULE_LABELS[m], journeys: js, failing, never, state: failing.length ? 'down' : never.length === js.length ? 'unknown' : 'up' };
    });
  }, [data]);

  if (err) return <p className="cs-help" role="alert">Platform health: {err}</p>;
  if (!data) return <p className="cs-help">Loading platform health…</p>;
  const { latest, runs, config, inFlight } = data;
  const failingCount = modules.reduce((a, m) => a + m.failing.length, 0);
  const totalJourneys = data.registry.length;
  const staleMs = (config.intervalSec || 900) * 1000 * 2.5;
  const stale = latest && Date.now() - latest.at > staleMs;

  return (
    <div data-nova-note="Platform feature health: every nventr.io feature has a synthetic journey that runs every 15 minutes as a probe member. Red modules have a failing journey; click one to see which and why. Run now queues an immediate run.">
      <h2>
        Platform feature health{' '}
        <span className="cs-help">{totalJourneys} journeys · every {Math.round((config.intervalSec || 900) / 60)} min · incident after {config.failThreshold} failing runs</span>
      </h2>

      <div className="aa-health-strip">
        <div className={`aa-health-stat ${latest ? (latest.ok ? 'is-ok' : 'is-err') : ''}`}>
          <strong>{latest ? (latest.ok ? 'All green' : `${latest.totals?.failed || failingCount} failing`) : '—'}</strong>
          {latest ? `last run ${fmtAgo(latest.at)}${stale ? ' · STALE' : ''}` : 'no run yet'}
        </div>
        <div className="aa-health-stat"><strong>{latest?.totals ? `${latest.totals.passed}/${latest.totals.total}` : '—'}</strong>journeys passed</div>
        <div className="aa-health-stat"><strong>{latest?.consecutiveFailures || 0}</strong>consecutive failing runs</div>
        <div className="aa-health-stat"><strong>${latest?.modelUsd?.perDay?.toFixed(2) ?? '0.00'}</strong>model spend / day{latest?.overBudget ? ' · over budget, model journeys skipped' : ` (cap $${config.modelBudgetUsdPerDay})`}</div>
        {latest?.incidentId ? <div className="aa-health-stat is-err"><strong>incident</strong>{latest.incidentId}</div> : null}
        <div className="aa-health-stat">
          <button type="button" className="cs-btn cs-btn-sm" disabled={busy || !!inFlight} onClick={onRun}>
            {inFlight ? `running · ${inFlight.stage || inFlight.status}` : 'Run now'}
          </button>
          {!config.probeOrgId ? <span className="aa-warn-text"> probe org not seeded (scripts/seed-probe-org.mjs)</span> : null}
          {config.enabled === false ? <span className="aa-warn-text"> scheduler disabled (platform.health.enabled)</span> : null}
        </div>
      </div>

      <div className="aa-ph-grid">
        {modules.map((m) => (
          <button
            key={m.id}
            type="button"
            className={`aa-ph-module is-${m.state} ${open === m.id ? 'is-open' : ''}`}
            onClick={() => setOpen(open === m.id ? null : m.id)}
            aria-expanded={open === m.id}
          >
            <span className="aa-ph-module-name">{m.label}</span>
            <span className={`aa-health-badge is-${m.state === 'up' ? 'up' : m.state === 'down' ? 'down' : 'unknown'}`}>
              {m.state === 'down' ? `${m.failing.length} failing` : m.state === 'up' ? `${m.journeys.length} ok` : 'no data'}
            </span>
          </button>
        ))}
      </div>

      {open ? (
        <div className="cs-card aa-ph-detail">
          <h3>{MODULE_LABELS[open]} <span className="cs-help">{modules.find((m) => m.id === open)?.journeys.length} journeys</span></h3>
          <table className="cs-table">
            <thead><tr><th>Journey</th><th>Kind</th><th>Last</th><th>Took</th><th>Streak</th><th>Pass rate</th><th>Detail</th></tr></thead>
            <tbody>
              {modules.find((m) => m.id === open)?.journeys.map((j) => (
                <tr key={j.id} className={j.ok === false ? 'aa-incident-open' : ''}>
                  <td><span className="aa-mono">{j.id}</span><br /><span className="cs-help">{j.label}</span></td>
                  <td>{KIND_HELP[j.kind] || j.kind}</td>
                  <td>{j.ok === undefined ? <span className="aa-health-badge is-unknown">never</span> : <span className={`aa-health-badge is-${j.ok ? 'up' : 'down'}`}>{j.ok ? 'pass' : 'fail'}</span>}<br /><span className="cs-help">{fmtAgo(j.lastAt)}</span></td>
                  <td>{fmtMs(j.lastMs)}</td>
                  <td>{j.failStreak ? <span className="aa-warn-text">{j.failStreak} failing</span> : (j.lastOkAt ? 'ok' : '—')}</td>
                  <td>{j.runs ? `${Math.round((100 * (j.passes || 0)) / j.runs)}% of ${j.runs}` : '—'}</td>
                  <td className="aa-ph-err">{j.ok === false ? j.lastError : (j.lastDetail || '')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      <h3 className="aa-ph-h3">Recent runs <span className="cs-help">last {runs.length}</span></h3>
      <div className="aa-ph-runs">
        {runs.map((r) => (
          <button
            key={r.id}
            type="button"
            className={`aa-ph-run is-${r.ok ? 'up' : 'down'} ${run?.id === r.id ? 'is-open' : ''}`}
            title={`${new Date(r.at).toLocaleString()} · ${r.totals?.passed}/${r.totals?.total}${r.requestedBy ? ` · ${r.requestedBy}` : ''}`}
            onClick={() => setRun(run?.id === r.id ? null : r)}
            aria-label={`run ${fmtAgo(r.at)} ${r.ok ? 'passed' : 'failed'}`}
          />
        ))}
      </div>
      {run ? (
        <div className="cs-card aa-ph-detail">
          <h3>{new Date(run.at).toLocaleString()} <span className="cs-help">{run.totals?.passed}/{run.totals?.total} · {fmtMs(run.totals?.ms)} total{run.requestedBy ? ` · ${run.requestedBy}` : ''}{run.overBudget ? ' · model journeys skipped (budget)' : ''}</span></h3>
          {run.failing.length ? (
            <ul className="aa-ph-fail-list">{run.failing.map((f) => <li key={f.id}><span className="aa-mono">{f.id}</span> — {f.error}</li>)}</ul>
          ) : <p className="cs-help">Every journey passed.</p>}
        </div>
      ) : null}
    </div>
  );
}
